import { useEffect, useRef, useState } from 'react'

/**
 * Subscribes the big screen to the event — slides 15 to 17 and 26.
 *
 * Unlike the student pass and the Admin dashboard, this needs no ticket and
 * no session: the screen is a projector in the hall and the route is public
 * and read-only. Whoever sets the hall up plugs in a laptop and opens the
 * URL — nothing to sign into, nothing to go wrong five minutes before the
 * ceremony.
 *
 * Because there is no ticket to spend, `EventSource` can do its own
 * reconnection, which is more robust than anything written here. The last
 * payload is still held and re-shown so a Wi-Fi blip never blanks the screen
 * mid-ceremony.
 */
export function useScreenStream<T>(enabled = true) {
  const [data, setData] = useState<T | null>(null)
  const [state, setState] = useState<'connecting' | 'live' | 'error'>('connecting')

  const latest = useRef<T | null>(null)

  useEffect(() => {
    if (!enabled) return

    const source = new EventSource('/api/screen/stream')

    source.addEventListener('screen', (event) => {
      const next = JSON.parse((event as MessageEvent).data) as T
      latest.current = next
      setData(next)
      setState('live')
    })

    source.addEventListener('open', () => setState('live'))
    source.onerror = () => setState('error')

    return () => source.close()
  }, [enabled])

  return { data: data ?? latest.current, state }
}
